import { useEffect, useMemo, useState } from "react";
import { Search, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatBRL, resolveImageUrl } from "../../lib/api";
import { useCart } from "../../context/CartContext";

function ProductCard({ product }) {
  const { addItem } = useCart();
  const outOfStock = (product.stock !== null && product.stock !== undefined && product.stock <= 0) || product.status === "indisponivel";
  const onOrder = product.status === "sob_encomenda";
  const hasPromo = product.promo_price && product.promo_price < product.price;
  const effectivePrice = hasPromo ? product.promo_price : product.price;
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/p/${product.id}`;

  const share = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const text = `Olha esse produto: ${product.name} — ${formatBRL(effectivePrice)}\n${shareUrl}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: product.name, text, url: shareUrl });
        return;
      } catch {
        return; // usuário cancelou
      }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="bg-[var(--site-bg)] rounded-2xl border border-[#E8E2D9] p-4 flex flex-col justify-between group hover:border-[var(--brand)] transition-colors"
    >
      <Link to={`/produto/${product.id}`} data-testid={`product-link-${product.id}`} className="block">
        <div className="relative aspect-square rounded-xl overflow-hidden mb-4 bg-[#EDE6DB]">
          {product.image_url ? (
            <img
              src={resolveImageUrl(product.image_url)}
              alt={product.name}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-[#6B645C] font-mono text-xs uppercase tracking-widest">
              Sem foto
            </div>
          )}
          {product.badge && (
            <span className="absolute top-3 left-3 bg-[#1A1816] text-[var(--site-bg)] text-[10px] font-mono uppercase px-2 py-1 rounded">
              {product.badge}
            </span>
          )}
          {hasPromo && (
            <span className="absolute top-3 right-3 bg-[var(--brand)] text-white text-[10px] font-mono uppercase px-2 py-1 rounded">
              Promoção
            </span>
          )}
          {outOfStock && (
            <div className="absolute inset-0 bg-[var(--site-bg)]/70 flex items-center justify-center">
              <span className="bg-[#1A1816] text-white text-xs font-mono uppercase px-3 py-1.5 rounded-full">Indisponível</span>
            </div>
          )}
          {onOrder && !outOfStock && (
            <span className="absolute bottom-3 left-3 bg-amber-500 text-white text-[10px] font-mono uppercase px-2 py-1 rounded">
              Sob encomenda
            </span>
          )}
          <span
            role="button"
            tabIndex={0}
            onClick={share}
            onKeyDown={(e) => e.key === "Enter" && share(e)}
            data-testid={`share-product-${product.id}`}
            aria-label="Compartilhar produto"
            className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center text-[#1A1816] hover:bg-[var(--brand)] hover:text-white transition-colors"
          >
            <Share2 className="w-4 h-4" />
          </span>
        </div>
        <h3 className="font-display font-semibold text-base text-[#1A1816] mb-1">{product.name}</h3>
      </Link>
      <div>
        <div className="flex justify-between items-baseline mb-1 gap-2">
          <p className="text-xs text-[#6B645C] line-clamp-2">{product.description}</p>
          <span className="font-mono text-sm font-semibold text-[#1A1816] whitespace-nowrap text-right">
            {hasPromo && <span className="block text-[11px] text-[#A89E94] line-through">{formatBRL(product.price)}</span>}
            {formatBRL(effectivePrice)}
          </span>
        </div>
        <button
          type="button"
          onClick={() => addItem({ ...product, price: effectivePrice })}
          disabled={outOfStock}
          data-testid={`add-to-cart-${product.id}`}
          className="w-full mt-3 bg-[#1A1816] text-white py-2.5 rounded-xl font-display text-xs uppercase tracking-wider font-medium hover:bg-[var(--brand)] transition-colors disabled:opacity-50 disabled:hover:bg-[#1A1816]"
        >
          {outOfStock ? "Indisponível" : "Adicionar à Sacola"}
        </button>
      </div>
    </article>
  );
}

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api
      .get("/products")
      .then((res) => setProducts(res.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => [...new Set(products.map((p) => p.category).filter(Boolean))], [products]);

  const filtered = useMemo(
    () =>
      products.filter(
        (p) =>
          (!category || p.category === category) &&
          (!search || p.name.toLowerCase().includes(search.toLowerCase()))
      ),
    [products, category, search]
  );

  return (
    <section id="catalogo" className="py-24 px-6 max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--brand)] block mb-2">Vitrine Selecionada</span>
          <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight">Catálogo da Loja</h2>
        </div>
        <p className="text-[#5A534C] text-sm max-w-xs leading-relaxed">
          Adicione à sacola e finalize seu pedido direto com nossa equipe pelo WhatsApp.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-10">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#6B645C]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar produto..."
            data-testid="catalog-search-input"
            className="w-full bg-white border border-[#E8E2D9] rounded-full pl-11 pr-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors"
          />
        </div>
        <div className="flex flex-wrap gap-2" data-testid="catalog-category-filters">
          <button
            type="button"
            onClick={() => setCategory("")}
            data-testid="filter-all"
            className={`px-4 py-2 rounded-full font-mono text-xs uppercase tracking-wider border transition-colors ${
              !category ? "bg-[#1A1816] text-white border-[#1A1816]" : "border-[#D5CDC2] text-[#5A534C] hover:border-[#1A1816]"
            }`}
          >
            Todos
          </button>
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              data-testid={`filter-${c}`}
              className={`px-4 py-2 rounded-full font-mono text-xs uppercase tracking-wider border transition-colors ${
                category === c ? "bg-[#1A1816] text-white border-[#1A1816]" : "border-[#D5CDC2] text-[#5A534C] hover:border-[#1A1816]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-[#6B645C] text-sm" data-testid="catalog-loading">Carregando produtos...</p>
      ) : filtered.length === 0 ? (
        <div className="p-12 rounded-3xl border border-dashed border-[#D5CDC2] text-center" data-testid="catalog-empty">
          <p className="text-sm text-[#6B645C]">Nenhum produto encontrado. Novidades chegam em breve!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="catalog-grid">
          {filtered.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </section>
  );
}
